import type { Chat } from "../types";
import { isOwnParticipant } from "./messageOwnership";

/**
 * Which chats are waiting for the character's reply ("she is typing"), derived
 * from the participant-keyed `lastMessages` map on each chat document: the chat
 * is pending when the newest entry belongs to the user. The user is either
 * `chat.userId` or — for a chat carried over from an anonymous session at
 * sign-in — `chat.adoptedFromUid` (see isOwnParticipant).
 *
 * Lives outside AppProvider so it can be unit-tested without a React tree.
 */
export function computePendingByChatId(chats: Chat[]): Record<string, boolean> {
  return chats.reduce<Record<string, boolean>>((acc, chat) => {
    const lastMessages = chat.lastMessages ?? {};
    let latestParticipant: string | null = null;
    let latestTime = 0;

    Object.entries(lastMessages).forEach(([participantId, entry]) => {
      const createdAt = entry?.createdAt;
      const time = createdAt instanceof Date ? createdAt.getTime() : 0;
      if (time > latestTime) {
        latestTime = time;
        latestParticipant = participantId;
      }
    });

    if (latestParticipant !== null && isOwnParticipant(latestParticipant, chat)) {
      acc[chat.id] = true;
    }
    return acc;
  }, {});
}
